import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  Gift, Users, Ticket, Copy, CheckCircle2, ArrowLeft,
  Loader2, Share2, Clock, Tag, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const PromotionsPage = () => {
  const { token, user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [referralProgress, setReferralProgress] = useState(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/coupons/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCoupons(res.data.coupons || []);
      setReferralProgress(res.data.referral_progress);
    } catch (e) {
      toast.error('Error al cargar cupones');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const res = await axios.post(
        `${API}/coupons/redeem`,
        { code: redeemCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setRedeemCode('');
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al canjear cupon');
    } finally {
      setRedeeming(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/?ref=${user?.referral_code || ''}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace de referido copiado');
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/?ref=${user?.referral_code || ''}`;
    const msg = encodeURIComponent(`Prueba NumeroMaestro, el mejor sistema de analisis de loterias con Sócrates: ${link}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const progressPct = referralProgress
    ? Math.min((referralProgress.current_referrals / referralProgress.next_milestone) * 100, 100)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="animate-spin text-green-400" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-300">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Gift size={22} className="text-white" />
              </div>
              <h1 className="text-3xl font-black text-white" data-testid="promotions-title">Promociones</h1>
            </div>
            <p className="text-slate-500 text-sm mt-1 ml-14">Invita amigos y gana recompensas</p>
          </div>
        </div>

        {/* Referral Progress Card */}
        {referralProgress && (
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20 mb-6" data-testid="referral-progress-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Users size={20} className="text-green-400" />
                Programa de Referidos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">
                  {referralProgress.current_referrals} de {referralProgress.next_milestone} amigos invitados
                </span>
                <span className="text-green-400 font-bold">
                  {referralProgress.remaining === 0 ? 'Meta alcanzada!' : `Faltan ${referralProgress.remaining}`}
                </span>
              </div>
              <Progress value={progressPct} className="h-3" />
              <p className="text-slate-500 text-xs">
                Invita a {referralProgress.threshold} amigos y recibe 1 mes gratis de suscripcion Pro
              </p>

              {/* Share buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={copyReferralLink}
                  variant="outline"
                  size="sm"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  data-testid="copy-referral-btn"
                >
                  <Copy size={14} className="mr-1.5" />
                  Copiar Enlace
                </Button>
                <Button
                  onClick={shareWhatsApp}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 size={14} className="mr-1.5" />
                  Compartir por WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Redeem Coupon */}
        <Card className="bg-slate-900/80 border-slate-700/50 mb-6" data-testid="redeem-coupon-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Ticket size={18} className="text-amber-400" />
              Canjear Cupon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="Ingresa tu codigo (ej: NM-XXXX-XXXX)"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600"
                data-testid="coupon-code-input"
              />
              <Button
                onClick={handleRedeem}
                disabled={!redeemCode.trim() || redeeming}
                className="bg-amber-500 hover:bg-amber-600 text-white min-w-[120px]"
                data-testid="redeem-coupon-btn"
              >
                {redeeming ? <Loader2 size={16} className="animate-spin" /> : 'Canjear'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* My Coupons */}
        <div className="space-y-3">
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Tag size={18} className="text-purple-400" />
            Mis Cupones ({coupons.length})
          </h2>
          {coupons.length === 0 ? (
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardContent className="py-12 text-center">
                <Gift size={40} className="text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">Aun no tienes cupones</p>
                <p className="text-slate-600 text-sm mt-1">Invita amigos para ganar cupones gratis</p>
              </CardContent>
            </Card>
          ) : (
            coupons.map((coupon) => (
              <Card
                key={coupon.id}
                className={`border ${coupon.used ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-900/80 border-green-500/20'}`}
                data-testid={`coupon-${coupon.code}`}
              >
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      coupon.used ? 'bg-slate-800' : 'bg-green-500/20'
                    }`}>
                      {coupon.used
                        ? <CheckCircle2 size={20} className="text-slate-600" />
                        : <Sparkles size={20} className="text-green-400" />
                      }
                    </div>
                    <div>
                      <p className={`font-mono font-bold ${coupon.used ? 'text-slate-600' : 'text-white'}`}>
                        {coupon.code}
                      </p>
                      <p className="text-slate-500 text-xs">{coupon.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={coupon.used
                      ? 'bg-slate-800 text-slate-500'
                      : 'bg-green-500/20 text-green-400 border-green-500/30'
                    }>
                      {coupon.used ? 'Usado' : 'Activo'}
                    </Badge>
                    {!coupon.used && coupon.expires_at && (
                      <p className="text-slate-600 text-[10px] mt-1 flex items-center gap-1 justify-end">
                        <Clock size={10} />
                        Exp: {new Date(coupon.expires_at).toLocaleDateString('es-DO')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default PromotionsPage;
